import { Autocomplete, CircularProgress, TextField, useTheme } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useAppSelector, useNotification } from '../../store/redux/hooks'
import { setLeader, setLeaderId } from '../../store/redux/reducers/leaderSlice'
import { useLazyGetLightLeadersQuery, useLazyGetLightProjectLeadersQuery } from '../../store/redux/reducers/usersSlice'
import { User } from '../../store/types'
import { getLeaderPickerState } from '../../store/redux/reducers/pageHeaderSlice'

export type ProjectDistributionLeaderSelectSettings = {
  shown: boolean
  onlyProjectLeaders?: boolean
}

const ProjectDistributionLeaderSelectComponent = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { displayNotification } = useNotification()
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<readonly User[]>([])
  const [trigger, { isLoading: isGetLightLeadersLoading }] = useLazyGetLightLeadersQuery()
  const [triggerProjectLeaders, { isLoading: isGetLightProjectLeadersLoading }] = useLazyGetLightProjectLeadersQuery()
  const loading = isGetLightLeadersLoading || isGetLightProjectLeadersLoading
  const notificationRef = useRef(displayNotification)
  const { leader } = useAppSelector(state => state.leaderId)
  const onlyProjectLeaders = useAppSelector(getLeaderPickerState)?.onlyProjectLeaders

  useEffect(() => {
    notificationRef.current = displayNotification
  }, [displayNotification])

  useEffect(() => {
    let active = true

    const loadLeaders = async () => {
      const leaders: User[] = []
      let page = 0
      let fetchResults

      do {
        fetchResults = !onlyProjectLeaders
          ? await trigger({
              page: page,
              size: 10,
            })
          : await triggerProjectLeaders({ page: page, size: 10 })
        if (active) {
          if (fetchResults.isError) {
            notificationRef.current({ message: t('message.somethingWentWrong'), type: 'error' })
            setOpen(false)
            return
          }
          if (fetchResults.isSuccess) {
            leaders.push(...fetchResults.data.content)
            page += 1
            if (fetchResults.data.last || fetchResults.data.content.length === 0) {
              break
            }
          }
        }
      } while (!fetchResults?.data?.last)

      if (active) {
        setOptions(leaders)
      }
    }

    if (open && options.length === 0) {
      loadLeaders()
    }

    return () => {
      active = false
    }
  }, [open, trigger, t, options.length, onlyProjectLeaders, triggerProjectLeaders])

  useEffect(() => {
    if (!open) {
      setOptions([])
    }
  }, [open])

  const handleLeaderChange = (user: User | null) => {
    if (user) {
      dispatch(setLeaderId(user.id))
      dispatch(setLeader(user))
    } else {
      dispatch(setLeaderId(undefined))
      dispatch(setLeader(undefined))
    }
  }

  return (
    <Autocomplete
      id="asynchronous-demo"
      sx={{ width: 250, pl: '8px' }}
      open={open}
      onOpen={() => {
        setOpen(true)
      }}
      onClose={() => {
        setOpen(false)
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={option => `${option.firstName} ${option.lastName}`}
      options={options}
      loading={loading}
      loadingText={t('components.loading.loading')}
      noOptionsText={t('page.projectDistribution.leadersNotFound')}
      value={leader ?? null}
      onChange={(_event, value) => handleLeaderChange(value)}
      renderInput={params => (
        <TextField
          fullWidth
          size="small"
          inputProps={{ ...params.inputProps, 'aria-label': t('page.projectDistribution.leader') }}
          label={t('page.projectDistribution.leader')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress sx={{ color: theme.palette.primary.main }} size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  )
}

export default ProjectDistributionLeaderSelectComponent
